import { ClockCircleOutlined } from '@ant-design/icons';
import { Card, Skeleton } from 'antd';
import { useTranslation } from 'react-i18next';

import {
  TiendaPerfectaRouteReportContext,
  TiendaPerfectaRouteReportProgress,
} from 'models/TiendaPerfecta';

import { formatInstant, formatLongDate } from '../utils/time';
import AnimatedNumber from './AnimatedNumber';

const I18N_BASE = 'scenes.route_report.components.progress';

interface RouteReportProgressProps {
  progress: TiendaPerfectaRouteReportProgress | null;
  context: TiendaPerfectaRouteReportContext | null;
  userTimeZone: string;
  loading?: boolean;
}

/**
 * Avance real del día con su proyección de cierre.
 *
 * El marcador de proyección es una MUESCA: parte la barra con un hueco del color
 * de la superficie y clava una línea dentro. No depende de ningún contraste,
 * porque es una ausencia de relleno y no un color sobre otro — y eso importa
 * acá, donde el relleno usa `colorPrimary` y cada compañía elige el suyo. Con un
 * marcador de color fijo, un primario navy lo haría desaparecer justo cuando el
 * avance supera la proyección y la muesca cae encima del relleno.
 */
function RouteReportProgress({
  progress,
  context,
  userTimeZone,
  loading = false,
}: RouteReportProgressProps) {
  const { t } = useTranslation();

  if (loading || !progress || !context) {
    return (
      <Card>
        <div className="route-report-progress__top">
          <div>
            <div className="route-report-progress__label">
              {t(`${I18N_BASE}.title`)}
            </div>
            <div className="route-report-progress__headline">
              <Skeleton.Input active style={{ width: 104, height: 44 }} />
              <Skeleton.Input active style={{ width: 212, height: 25 }} />
            </div>
          </div>
          <div className="route-report-progress__right">
            <span className="route-report-progress__label">
              {t(`${I18N_BASE}.projection_title`)}
            </span>
            <Skeleton.Input active style={{ width: 186, height: 27 }} />
          </div>
        </div>
        <div className="route-report-progress__bar" />
      </Card>
    );
  }

  const { realProgress, projection } = progress;
  const hasProjection = projection !== null;
  // `serverTime` ya viene con offset; se muestra en la zona del usuario igual
  // que todo lo demás. Pasa por el helper y no por `dayjs` directo: acá el
  // plugin `timezone` no está extendido y solo funcionaba de rebote.
  const cutTime = formatInstant(context.serverTime, userTimeZone, 'HH:mm');
  // Una sola fecha larga y localizada para el corte y para el pie: la
  // preposición no puede quedar clavada en español dentro de un formato.
  const dateLabel = formatLongDate(context.date);

  return (
    <Card>
      <div className="route-report-progress__top">
        <div>
          <div className="route-report-progress__label">
            {t(`${I18N_BASE}.title`)}
          </div>
          <div className="route-report-progress__headline">
            <AnimatedNumber
              className="route-report-progress__value"
              value={realProgress}
              format={(n) => `${n}%`}
              showDelta
            />
            <span className="route-report-progress__chip">
              <ClockCircleOutlined />
              {t(`${I18N_BASE}.cut`, { date: dateLabel, time: cutTime })}
            </span>
          </div>
        </div>

        <div className="route-report-progress__right">
          <span className="route-report-progress__label">
            {t(`${I18N_BASE}.projection_title`)}
          </span>
          {hasProjection ? (
            <span className="route-report-progress__projection">
              {t(`${I18N_BASE}.projection_value`, { value: projection })}
            </span>
          ) : (
            <span className="route-report-progress__projection route-report-progress__projection--none">
              {t(`${I18N_BASE}.projection_none`)}
            </span>
          )}
        </div>
      </div>

      <div className="route-report-progress__bar">
        <div
          className="route-report-progress__fill"
          style={{ width: `${realProgress}%` }}
        />
        {hasProjection && (
          <div
            className="route-report-progress__mark"
            style={{ left: `calc(${projection}% - 4.5px)` }}
          />
        )}
      </div>

      <div className="route-report-progress__foot">
        {hasProjection ? (
          <span className="route-report-progress__legend">
            <i />
            {t(`${I18N_BASE}.legend`)}
          </span>
        ) : (
          <span />
        )}
        <span>{t(`${I18N_BASE}.disclaimer`, { day: dateLabel })}</span>
      </div>
    </Card>
  );
}

export default RouteReportProgress;
